import { z } from 'zod'

// Order Item Schema - matches backend API format with coercion
export const OrderItemSchema = z.object({
  product_id: z.string(),
  product_name: z.string(),
  quantity: z.coerce.number().positive(),
  unit: z.string(), // Unit of measurement (Case, Box, Each, DZ, RL, etc.)
  unit_price: z.coerce.number().nonnegative(),
  total_price: z.coerce.number().nonnegative()
})

// Create Order Request Schema - matches backend API expectations
export const CreateOrderRequestSchema = z.object({
  title: z.string().min(1),
  project_id: z.number().int().positive().optional(),
  job_number: z.string().optional(),
  delivery_date: z.string().optional(),
  notes: z.string().optional(),
  created_at: z.string().optional(), // Custom creation date
  items: z.array(OrderItemSchema).optional()
})

// Update Order Request Schema - matches backend API expectations
export const UpdateOrderRequestSchema = z.object({
  title: z.string().min(1).optional(),
  project_id: z.number().int().positive().optional(),
  job_number: z.string().optional(),
  delivery_date: z.string().optional(),
  notes: z.string().optional(),
  status: z.enum(['pending', 'active', 'completed', 'cancelled']).optional(),
  items: z.array(OrderItemSchema.extend({
    id: z.number().optional() // For existing items
  })).optional()
})

// Order Response Schema - what backend returns (with proper coercion and nullability)
export const OrderResponseSchema = z.object({
  id: z.coerce.number(),
  title: z.string(),
  status: z.enum(['pending', 'active', 'completed', 'cancelled']),
  amount: z.coerce.number(),
  created_at: z.string(),
  user_id: z.coerce.number(),
  project_id: z.coerce.number().nullable().optional(),
  job_number: z.string().nullable().optional(),
  delivery_date: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  total_cost: z.coerce.number().nullable().optional(),
  items: z.array(z.object({
    id: z.coerce.number(),
    product_id: z.string(),
    product_name: z.string(),
    quantity: z.coerce.number(),
    unit: z.string(), // Unit of measurement (Case, Box, Each, DZ, RL, etc.)
    unit_price: z.coerce.number(),
    total_price: z.coerce.number()
  })).optional(),
  project: z.object({
    id: z.coerce.number(),
    name: z.string(),
    code: z.string()
  }).optional()
})

// Query Parameters Schema for GET requests
export const OrderQuerySchema = z.object({
  status: z.enum(['pending', 'active', 'completed', 'cancelled']).optional(),
  job_number: z.string().optional(),
  project_id: z.string().optional(), // String because URL params are strings
  id: z.string().optional() // String because URL params are strings
})

// Export Request Schema
export const ExportRequestSchema = z.object({
  format: z.enum(['pdf', 'excel', 'csv']).default('pdf')
})

// Export Response Schema
export const ExportResponseSchema = z.object({
  success: z.boolean(),
  downloadUrl: z.string().optional(),
  fileName: z.string().optional(),
  error: z.string().optional()
})

// Export TypeScript types
export type OrderItem = z.infer<typeof OrderItemSchema>
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>
export type UpdateOrderRequest = z.infer<typeof UpdateOrderRequestSchema>
export type OrderResponse = z.infer<typeof OrderResponseSchema>
export type OrderQuery = z.infer<typeof OrderQuerySchema>
export type ExportRequest = z.infer<typeof ExportRequestSchema>
export type ExportResponse = z.infer<typeof ExportResponseSchema>

// Helper types for internal use
export type OrderItemWithId = OrderItem & { id?: number }
export type OrderListResponse = OrderResponse[] 